import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useI18n } from "@/hooks/useI18n";
import { ItineraryData, IItineraryData } from "@/lib/builder/models/itinerary.model";
import DayBuilderStep from "@/lib/builder/DayBuilderStep";
import CheckoutStep from "@/lib/builder/CheckoutStep";
import ItinerarySidebar from "@/lib/builder/ItinerarySidebar";
import { TOTAL_STEPS } from "@/lib/builder/models/builder-config.model";
import { supabase } from "@/integrations/supabase/client";
import { showDevError } from "@/components/ui/dev-error-toast";
import { toast } from "@/hooks/useToast";
import { TravelRequestModal } from "@/lib/requests";
import { AccommodationActivityClass, ItineraryActivity } from "@/lib/builder/models/activity.model";
import { AuthContext } from "@/lib/auth/models/auth.model";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { CreateQuoteModal } from "@/lib/itineraries/CreateQuoteModal";
import { DashboardItinerary } from "@/lib/models";
import { useModal } from "@/contexts/ModalContext";
import { RequestChangesModal } from "@/lib/builder/modals/RequestChangesModal";
import { BookTripModal } from "@/lib/builder/modals/BookTripModal";
import { ItineraryStepModal } from "@/lib/builder/modals/ItineraryStepModal";
import { useItineraries } from "@/hooks/useItineraries";
import { useTravelRequests } from "@/hooks/useTravelRequests";
import { createCheckInOutActivities } from "@/components/utils/createCheckOutFromCheckIn";
import BuilderLayout from "@/components/layout/builder/BuilderLayout";

const ItineraryBuilder = () => {
    // Funzione helper per salvataggio attività con diff
    const saveActivitiesWithDiff = async (itineraryId: string, builderActivities: ItineraryActivity[]) => {
      // Recupera le attività già salvate a DB
      const dbActivities = await fetchItineraryActivities(itineraryId);
      // Calcola le differenze
      const { diffActivities } = await import("@/lib/builder/utils/diffActivities");
      const { toInsert, toUpdate, toDelete } = diffActivities(builderActivities, dbActivities);
      // Salva con la nuova interfaccia
      return await saveItineraryActivities(itineraryId, toInsert, toUpdate, toDelete);
    };
  const { t } = useI18n();
  const { openModal } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestId = searchParams.get('requestId');
  const itineraryId = searchParams.get('itineraryId');
  const stepFromUrl = searchParams.get('step');


  // Funzioni specifiche per l'ItineraryBuilder (senza caricare tutti i dati)
  const { saveItinerary, fetchItineraryActivities, saveItineraryActivities, updateItineraryStatus } = useItineraries({ disableAutoFetch: true });
  const { fetchTravelRequest } = useTravelRequests({ disableAutoFetch: true });

  // Get user role from AuthContext
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useItineraries deve essere usato all\'interno di un AuthProvider');
  }
  const userRole = authContext.userRole;

  // Step navigation state - Initialize from URL if present
  const [currentStep, setCurrentStep] = useState(() => {
    return stepFromUrl ? parseInt(stepFromUrl) : 1;
  });
  const [canProceed, setCanProceed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isTravelRequestModalOpen, setIsTravelRequestModalOpen] = useState(false);
  const [isTripQuoteModalOpen, setIsTripQuoteModalOpen] = useState(false);
  const [isItineraryModalOpen, setIsItineraryModalOpen] = useState(false);
  const [isRequestChangesModalOpen, setIsRequestChangesModalOpen] = useState(false);
  const [isBookTripModalOpen, setIsBookTripModalOpen] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [itineraryBuilder, setItineraryBuilder] = useState<any>(null);

  // Form data state - Initialize with empty data
  const [itineraryData, setItineraryData] = useState<ItineraryData>();

  const changeStep = useCallback((step: number, resetDate: boolean = false) => {
    setCurrentStep(step);
    if (resetDate && step === 2 && itineraryData) {
      setSelectedDate(itineraryData.firstDay);
    }
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('step', step.toString());
      if (resetDate && step === 2 && itineraryData) {
        newParams.set('date', itineraryData.firstDay);
      }
      return newParams;
    });
  }, [itineraryData, setSearchParams]);

  // Ref per stabilizzare changeStep nelle funzioni di inizializzazione
  const changeStepRef = useRef(changeStep);
  changeStepRef.current = changeStep;

  // Main function to initialize itinerary data
  const initializeItineraryData = useCallback(async (requestId: string) => {
    setIsLoading(true);
    try {
      // 1. Fetch travel request
      const travelRequest = await fetchTravelRequest(requestId);

      if (!travelRequest) {
        return;
      }

      const itineraryData = new ItineraryData({} as IItineraryData);
      itineraryData.fetchTravelRequest(travelRequest);
      setIsReadOnly(false); // New itineraries are always editable

      setItineraryData(itineraryData);
      setSelectedDate(itineraryData.firstDay);
      changeStepRef.current(1, true);

    } catch (error) {
      console.error('Error initializing itinerary data:', error);
      toast({
        title: t('builder.initializationError'),
        description: t('builder.initializationErrorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [fetchTravelRequest]);

  // Function to initialize from existing itinerary ID
  const initializeFromItineraryId = useCallback(async (itineraryId: string) => {
    setIsLoading(true);
    try {
      // Fetch the itinerary from database
      const { data: itinerary, error: itineraryError } = await supabase
        .from('itineraries')
        .select('*')
        .eq('id', itineraryId)
        .single();

      if (itineraryError) {
        console.error('Error fetching itinerary by ID:', itineraryError);
        showDevError({ error: itineraryError, context: 'fetching itinerary by ID' });
        toast({
          title: t('builder.loadError'),
          description: t('builder.loadErrorDesc'),
          variant: "destructive",
        });
        return;
      }

      // Fetch the travel request
      const travelRequest = await fetchTravelRequest(itinerary.travel_request_id);
      if (!travelRequest) {
        return;
      }

      // Fetch activities
      const activities: ItineraryActivity[] = await fetchItineraryActivities(itineraryId);

      // Create ItineraryData with existing data
      const itineraryData = new ItineraryData(itinerary as IItineraryData);

      // per ogni attività di accommodation modifico il vettore attivities andando a creare le accomodation checkout per ogni accommodation
      const modifiedActivities: ItineraryActivity[] = activities.flatMap((activity: ItineraryActivity) => {
        if (activity instanceof AccommodationActivityClass) {
          // createCheckInOutActivities returns two entries (check-in, check-out)
          const [checkIn, checkOut] = createCheckInOutActivities(activity as AccommodationActivityClass);
          return [checkIn, checkOut];
        }
        return activity as ItineraryActivity;
      });

      itineraryData.travel_request = new TravelRequest(travelRequest);
      itineraryData.setItineraryDays(modifiedActivities as ItineraryActivity[]);

      // Check if itinerary is read-only based on status and user role
      setIsReadOnly(userRole === 'traveler');

      setItineraryData(itineraryData);
      setSelectedDate(itineraryData.firstDay);

    } catch (error) {
      console.error('Error initializing from itinerary ID:', error);
      toast({
        title: t('builder.initializationError'),
        description: t('builder.loadItineraryErrorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [fetchTravelRequest, fetchItineraryActivities, userRole]);

  // Ref per stabilizzare le funzioni di inizializzazione
  const initializeItineraryDataRef = useRef(initializeItineraryData);
  initializeItineraryDataRef.current = initializeItineraryData;

  const initializeFromItineraryIdRef = useRef(initializeFromItineraryId);
  initializeFromItineraryIdRef.current = initializeFromItineraryId;

  // Step navigation handlers
  const handleNextStep = async () => {
    if (currentStep < TOTAL_STEPS && itineraryData) {
      const newStep = currentStep + 1;
      changeStep(newStep, true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep <= 1) return;
    const newStep = currentStep - 1;

    changeStep(newStep);
  };

  const handleDataChange = (newData: Partial<IItineraryData>) => {
    if (isReadOnly) return;
    setItineraryData((prev: ItineraryData) => prev.update(newData));
  };

  const getSyncedItineraryData = useCallback((baseData: ItineraryData) => {
    if (!itineraryBuilder) {
      return baseData;
    }

    return baseData.update({
      itinerary_days: itineraryBuilder.toArray()
    });
  }, [itineraryBuilder]);

  const handleSaveDraft = async () => {
    if (!itineraryData) return;

    setIsSaving(true);
    try {
      let syncedData = getSyncedItineraryData(itineraryData);

      // Always convert rejected status to draft when saving
      let dataToSave = syncedData;
      if (syncedData.status === 'rejected') {
        dataToSave = dataToSave.update({ status: 'draft' });
      }
      
      // Set the ID if we have it from URL parameter
      if (itineraryId && !dataToSave.id) {
        dataToSave = dataToSave.update({ id: itineraryId });
      }

      setItineraryData(dataToSave);

      const savedItinerary = await saveItinerary(dataToSave);

      if (savedItinerary) {
        const builderActivities = dataToSave.extractAllActivities();
        const activitiesSaved = await saveActivitiesWithDiff(savedItinerary.id, builderActivities);
        if (activitiesSaved) {
          exitBuilder();
        } else {
          console.error('Failed to save activities');
        }
      }
    } catch (error) {
      console.error('Error saving itinerary:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const exitBuilder = () => {
    navigate('/dashboard');
  };

  const sendItineraryForReview = async () => {
    if (!itineraryData) return;

    // Just open the quote modal without saving
    // The actual save will happen when the user confirms in the modal
    setIsTripQuoteModalOpen(true);
  };

  const handleCancel = () => {
    exitBuilder();
  };

  // Maps ItineraryData to a DashboardItinerary-compatible shape for the preview modal
  const mapToDashboardItinerary = (data: ItineraryData): DashboardItinerary => {
    const tr = data.travel_request;
    return {
      id: data.id || '',
      travelRequestId: data.travel_request_id || '',
      title: data.title,
      status: data.status || 'draft',
      startDate: data.start_date || null,
      endDate: data.end_date || null,
      guestsData: data.guests_data || [],
      budget: data.budget || 0,
      description: data.description || '',
      image: data.image || null,
      note: data.note || null,
      createdAt: '',
      updatedAt: '',
      total: data.total ?? null,
      commisionableTotal: data.commisionable_total ?? null,
      notCommisionableTotal: data.not_commisionable_total ?? null,
      p2bCommissionPercentage: data.p2b_commission_percentage ?? null,
      p2bCommissionAmount: data.p2b_commission_amount ?? null,
      tdFeePercentage: data.td_fee_percentage ?? null,
      travelRequest: {
        id: tr?.id || '',
        userId: tr?.user_id || null,
        destinations: tr?.destinations || [],
        departurePoint: tr?.departure_point || null,
        needsTransportation: tr?.needs_transportation || false,
        totalGuests: tr?.total_guests || data.guests_data?.length || 1,
        guestsData: (tr?.guests_data as any[]) || data.guests_data || [],
        travelDatesFlexible: tr?.travel_dates_flexible || false,
        travelStartDate: tr?.travel_start_date || data.start_date || null,
        travelEndDate: tr?.travel_end_date || data.end_date || null,
        budgetMin: tr?.budget_min ?? null,
        budgetMax: tr?.budget_max ?? null,
        budgetFlexible: tr?.budget_flexible || false,
        interests: tr?.interests || [],
        additionalNotes: tr?.additional_notes || null,
        contact: {
          firstName: tr?.contact_first_name || '',
          lastName: tr?.contact_last_name || '',
          email: tr?.contact_email || '',
          phone: tr?.contact_phone || '',
        },
        status: (tr?.status as any) || 'assigned',
        assignedAdvisorId: tr?.assigned_designer_id || null,
        preferredAdvisorId: tr?.travel_designer_preference || null,
        createdAt: tr?.created_at || '',
        updatedAt: tr?.updated_at || '',
      },
      assignedAdvisor: null,
      preferredAdvisor: null,
    } as unknown as DashboardItinerary;
  };

  // Sidebar handlers
  const handleEditTrip = () => {
    if (isReadOnly || userRole === 'traveler') {
      openModal('itinerary-details', {
        itinerary: mapToDashboardItinerary(itineraryData),
        onViewItinerary: () => {},
        showButton: false,
      });
    } else {
      setIsItineraryModalOpen(true);
    }
  };

  const handleItineraryModalClose = () => {
    if (itineraryData && !isReadOnly) {
      const newItineraryData = itineraryData.updateItineraryDays(itineraryData.start_date, itineraryData.end_date);
      setItineraryData(newItineraryData);
    }
    setIsItineraryModalOpen(false);
  };

  const handleDaySelect = (date: string) => {
    setSelectedDate(date);
    // Update URL with selected date
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('date', date);
      return newParams;
    });
  };

  useEffect(() => {
    if (itineraryId) {
      // If we have an itineraryId, load the existing itinerary
      initializeFromItineraryIdRef.current(itineraryId);
    } else if (requestId) {
      // If we have a requestId, try to load existing or create new
      initializeItineraryDataRef.current(requestId);
    }
  }, [requestId, itineraryId]);

  const handleViewTripRequest = () => {
    setIsTravelRequestModalOpen(true);
  };

  // Handle quote creation - save itinerary with pending status
  const handleSendItineraryInReview = async (): Promise<boolean> => {
    if (!itineraryData) return false;

    setIsSaving(true);
    try {
      let syncedData = getSyncedItineraryData(itineraryData);

      // Set the ID if we have it from URL parameter and update status
      let dataToSave = syncedData.update({ status: 'pending' });
      if (itineraryId && !dataToSave.id) {
        dataToSave = dataToSave.update({ id: itineraryId });
      }
      setItineraryData(dataToSave);

      // Save itinerary with pending status
      const savedItinerary = await saveItinerary(dataToSave);

      if (savedItinerary) {
        // Note: Admin notification is handled by database trigger
        const builderActivities = dataToSave.extractAllActivities();
        const activitiesSaved = await saveActivitiesWithDiff(savedItinerary.id, builderActivities);
        if (activitiesSaved) {
          exitBuilder();
          return true;
        } else {
          console.error('Failed to save activities');
        }
      }
      return false;
    } catch (error) {
      console.error('Error saving itinerary:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle quote modal close
  const handleQuoteModalClose = () => {
    setIsTripQuoteModalOpen(false);
  };

  const handleRequestChanges = () => {
    exitBuilder();
  };


  // Set canProceed based on current step and data completeness
  useEffect(() => {
    // For step 1 (DayBuilderStep), canProceed is managed by DayBuilderStep via setCanProceed
    // For step 2 (Checkout), canProceed is always true
    if (currentStep === 2) {
      setCanProceed(true);
    }
  }, [itineraryData, currentStep, isLoading]);

  // Handler approva/rigetta
  const handleAcceptItinerary = async (note?: string) => {
    if (!itineraryData?.id) return;
    try {
      await updateItineraryStatus(itineraryData.id, 'approved', note);
      exitBuilder();
    } catch (error) {
      console.error('Error accepting itinerary:', error);
      toast({
        title: t('builder.errorAccepting'),
        description: t('builder.errorAcceptingDesc'),
        variant: "destructive",
      });
    }
  };

  const handleRejectItinerary = async (note?: string) => {
    if (!itineraryData?.id) return;
    try {
      await updateItineraryStatus(itineraryData.id, 'rejected', note);
      exitBuilder();
    } catch (error) {
      console.error('Error rejecting itinerary:', error);
      toast({
        title: t('builder.errorRejecting'),
        description: t('builder.errorRejectingDesc'),
        variant: "destructive",
      });
    }
  };

  // Get current step component
  const getCurrentStepComponent = () => {
    const stepProps = {
      data: itineraryData,
      onDataChange: handleDataChange,
      onNext: handleNextStep,
      onPrevious: handlePreviousStep,
      onSave: sendItineraryForReview,
      onCancel: handleCancel,
      canProceed,
      setCanProceed,
      onDaySelect: handleDaySelect,
      selectedDate,
      isReadOnly,
      onBuilderChange: setItineraryBuilder
    };

    switch (currentStep) {
      case 1:
        return <DayBuilderStep {...stepProps} />;
      case 2:
        return <CheckoutStep {...stepProps} />;
      default:
        return <DayBuilderStep {...stepProps} />;
    }
  };

  // Aggiorna la nota dell'itinerario
  const handleUpdateItineraryNote = async (note: string): Promise<void> => {
    if (!itineraryData) return;
    const updated = itineraryData.setItineraryNote(note);
    setItineraryData(updated);
    if (updated.id) {
      await saveItinerary(updated);
    }
  };

  return (
    <BuilderLayout
      onPreviousStep={handlePreviousStep}
      onNextStep={handleNextStep}
      onSave={sendItineraryForReview}
      onSaveDraft={handleSaveDraft}
      onRejectItinerary={handleRejectItinerary}
      onAcceptItinerary={handleAcceptItinerary}
      onCancel={handleCancel}
      onExit={handleCancel}
      onViewTripRequest={handleViewTripRequest}
      onBookTrip={() => setIsBookTripModalOpen(true)}
      onRequestChanges={() => setIsRequestChangesModalOpen(true)}
      totalPrice={itineraryData?.total}
      startDate={itineraryData?.start_date}
      endDate={itineraryData?.end_date}
      refundable={itineraryData?.refundable}
      isFirstStep={currentStep === 1}
      isLastStep={currentStep === TOTAL_STEPS}
      isRecapStep={currentStep === TOTAL_STEPS}
      canProceed={canProceed}
      showHeader={true}
      showFooter={true}
      isReadOnly={isReadOnly || userRole === 'traveler'}
      isLoading={isLoading}
      role={userRole ?? undefined}
      onUpdateItineraryNote={handleUpdateItineraryNote}
      itineraryStatus={itineraryData?.status}
    >
      <div className="flex h-full flex-col lg:flex-row">
        {/* Sidebar - Only visible on step 1 (Day Builder)
            On mobile we force it to appear above the main content (order-first, w-full)
            On desktop it behaves as a side column (lg:w-80, lg:order-none) */}
        {currentStep === 1 && itineraryData && (
          <div className="w-full lg:w-80 order-first lg:order-none">
            <ItinerarySidebar
              data={itineraryData}
              builder={itineraryBuilder}
              onEditTrip={handleEditTrip}
              onDaySelect={handleDaySelect}
              selectedDate={selectedDate}
              isReadOnly={isReadOnly || userRole === 'traveler'}
            />
          </div>
        )}

        {/* Main Content - make it order-last on mobile so sidebar is shown above */}
        <div className={`flex-1 flex justify-center overflow-y-auto ${currentStep === 1 ? 'lg:ml-0' : ''} order-last lg:order-none`}>
          <div className="w-full h-full">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                  <p className="text-gray-600">
                    {itineraryId ? t('builder.loadingExistingItinerary') : t('builder.loadingTravelRequestData')}
                  </p>
                </div>
              </div>
            ) : (
              getCurrentStepComponent()
            )}
          </div>
        </div>
      </div>

      {/* Travel Request Modal - Solo visualizzazione */}
      <TravelRequestModal
        isOpen={isTravelRequestModalOpen}
        onClose={() => setIsTravelRequestModalOpen(false)}
        travelRequest={itineraryData?.travel_request ?? null}
        showActions={false}
      />

      {/* Trip Quote Modal */}
      <CreateQuoteModal
        isOpen={isTripQuoteModalOpen}
        onClose={() => handleQuoteModalClose()}
        onSend={async () => { await handleSendItineraryInReview(); }}
      />

      {/* Request Changes Modal */}
      <RequestChangesModal
        isOpen={isRequestChangesModalOpen}
        onClose={() => setIsRequestChangesModalOpen(false)}
        onSend={handleRequestChanges}
        itineraryId={itineraryData?.id ?? ''}
      />

      {/* Book Trip Modal (placeholder) */}
      <BookTripModal
        isOpen={isBookTripModalOpen}
        onClose={() => setIsBookTripModalOpen(false)}
        itinerary={itineraryData ?? null}
      />

      {/* Itinerary Step Modal */}
      <ItineraryStepModal
        isOpen={isItineraryModalOpen}
        onClose={handleItineraryModalClose}
        data={itineraryData}
        onDataChange={handleDataChange}
        canProceed={canProceed}
        setCanProceed={setCanProceed}
        isReadOnly={isReadOnly || userRole === 'traveler'}
      />
    </BuilderLayout>
  );
};

export default ItineraryBuilder;
